import { Component, DestroyRef, inject } from '@angular/core';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { MatIcon } from '@angular/material/icon';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { DialogData } from '../../../../common/components/dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormField, MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { PasswordService } from '../../../../services/password/password.service';
import { VerificationService } from '../../../../services/verification/verification.service';
import { SbSpinnerComponent } from '../../sb-spinner/sb-spinner.component';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { KeybackupSettingsTestIds } from './keybackup-settings.test-ids';
import { passwordValidator } from '../../../../validators/passwordValidator';
import { AlertBoxComponent } from '../../../../common/components/alert-box/alert-box.component';
import { SuggestionDialog, SuggestionDialogData } from './suggestion-dialog/suggestion-dialog';

@Component({
  selector: 'app-schluesselverwaltung-settings',
  imports: [
    ButtonComponent,
    MatIcon,
    MatFormField,
    MatInput,
    MatIconButton,
    MatTooltip,
    MatFormFieldModule,
    MatSuffix,
    FormsModule,
    ReactiveFormsModule,
    SbSpinnerComponent,
    AlertBoxComponent
  ],
  templateUrl: './keybackup-settings.component.html',
  styleUrl: './keybackup-settings.component.scss'
})
/**
 * Komponente für die Schlüsselverwaltung in den Profileinstellungen.
 * Begründung: Es werden keine AFOs durch die Möglichkeit der Schlüsselverwaltung erfüllt,
 * da diese Funktionalität rein der Benutzerkonfiguration dient.
 */
export class KeybackupSettingsComponent {
  suggestedPassword = '';
  showSpinner = false;
  copied = false;
  public currentPassword = '';
  passwordErrors: string[] = [];
  isPasswordVisible = false;
  protected readonly testIds = KeybackupSettingsTestIds;
  protected readonly matDialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly passwordService = inject(PasswordService);
  private readonly clientService = inject(MatrixService);
  private readonly verificationService = inject(VerificationService);
  private readonly formBuilder = inject(FormBuilder);
  protected verifyForm = this.formBuilder.group({
    password: ['', [passwordValidator()]]
  });

  /**
   * Öffnet Dialog zur Bestätigung der Schlüsselrücksetzung
   */
  resetKeyPassword(): void {
    const dialogData: DialogData = {
      title: 'SCHLÜSSEL NEU VERGEBEN',
      svgIcon: 'circle-error',
      message: `
      Mit dem Zurücksetzen Ihres Sicherheitsschlüssels verlieren Sie unwiderruflich den Zugriff auf alle bisherigen Chatverläufe.
      Zudem erhalten Kontakte, die Sie zuvor verifiziert haben, Sicherheitswarnungen, bis Sie sich erneut mit ihnen verifizieren.<br><br>
      Bitte fahren Sie nur fort, wenn Sie sicher sind, dass Sie alle anderen Geräte und Ihren Sicherheitsschlüssel verloren haben.<br>
      Wenn Sie sich unsicher sind oder Fragen haben, wenden Sie sich vor dem Zurücksetzen des Schlüssels an die zuständige<br>
      Kontaktperson Ihrer Krankenkasse. <br><br>
      Bitte geben Sie zur Bestätigung "<b>NEUVERGEBEN</b>" ein.
    `,
      confirmText: 'Schlüssel neu vergeben',
      cancelText: 'Abbrechen',
      validationText: 'NEUVERGEBEN',
      needInput: true,
      cancelDataTestId: this.testIds.buttonCancelResetKey,
      confirmDataTestId: this.testIds.buttonConfirmPermission,
      dialogActionTestId: 'dialog_permission_actions',
      dialogContentTestId: 'dialog_permission_content',
      inputFieldTestId: this.testIds.resetInput
    };

    this.dialogService.openErrorDialog(dialogData, '40rem', '35rem', true).subscribe({
      next: inputValue => {
        if (inputValue) {
          this.openSuggestionDialog();
        }
      },
      error: () => {
        this.snackbarService.error('Fehler beim Zurücksetzen des Schlüssels');
      }
    });
  }

  /**
   * Bestätigt das vorgeschlagene Passwort
   */
  async confirmSuggestedPassword(password: string): Promise<void> {
    this.showSpinner = true;
    this.clientService.setVerificationPassword(password);
    await this.setKeybackup(true);
  }

  async verifyKeyBackup(): Promise<void> {
    this.showSpinner = true;
    if (!this.verifyForm.value.password) {
      this.showSpinner = false;
      throw new Error('No Password found when calling verifyKeyBackup');
    }
    this.clientService.setVerificationPassword(this.verifyForm.value.password);
    await this.setKeybackup();
  }

  openSuggestionDialog(): void {
    const dialogData: SuggestionDialogData = {
      title: 'SCHLÜSSEL NEU VERGEBEN',
      svgIcon: 'circle-warning',
      message:
        'Aus Sicherheitsgründen darf der Schlüssel nicht Ihrem TI-M Passwort entsprechen. <br>' +
        'Bewahren Sie den Sicherheitsschlüssel an einem sicheren Ort auf, zum Beispiel in einem Passwort-Manager. Der Schlüssel wird später nur eingeschränkt erneut angezeigt. <br>' +
        'Geht der Sicherheitsschlüssel verloren, können bestehenede Chats nicht wiederhergestellt werden - auch nicht durch Administratoren.',
      confirmText: 'Schlüssel einrichten',
      cancelText: 'Abbrechen'
    };

    this.dialogService
      .openWarningDialog(dialogData, '40rem', '38rem', true, SuggestionDialog)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(inputValue => {
        const inputValueType = typeof inputValue;
        if (inputValueType === 'string') {
          if (!inputValue) {
            this.snackbarService.error('Bitte Passwort eingeben');
            return;
          }
          this.openCopyPasswordDialog(inputValue);
        }
      });
  }

  openCopyPasswordDialog(password: string): void {
    const dialogData: DialogData = {
      title: 'SCHLÜSSEL NEU VERGEBEN',
      svgIcon: 'circle-warning',
      message:
        'Bewahren Sie den Sicherheitsschlüssel an einem sicheren Ort auf, zum Beispiel in einem Passwort-Manager.' +
        ' Der Schlüssel wird später nur eingeschränkt erneut angezeigt. <br><br>' +
        'Geht der Sicherheitsschlüssel verloren, können bestehenede Chats nicht wiederhergestellt werden <br>' +
        '- auch nicht durch Administratoren.',

      confirmText: 'Bestätigen',
      cancelText: 'Abbrechen',

      needInput: true,
      requireCopy: true,

      displayAsPlainText: true,
      prefilledInput: password,

      inputPlainHeader: 'Schlüssel',

      confirmDataTestId: this.testIds.buttonConfirmFinal,
      cancelDataTestId: this.testIds.buttonCancelFinal,
      dialogActionTestId: 'dialog_final_actions',
      dialogContentTestId: 'dialog_final_content'
    };

    this.dialogService.openWarningDialog(dialogData, '30.5rem', '32rem', true).subscribe(async result => {
      if (result) {
        await this.confirmSuggestedPassword(password);
      }
    });
  }

  private async setKeybackup(reset = false): Promise<void> {
    try {
      await this.verificationService.initSecretStorage(reset);
      await this.verificationService.initCrossSigning(reset);
      await this.verificationService.initKeyBackup(reset);
      this.showSpinner = false;
      this.showSuccessDialog();
      await this.verificationService.initHydrateDevice(reset);
      this.clientService.setVerificationPassword(undefined);
    } catch {
      this.showSpinner = false;
      this.snackbarService.error('Fehler beim Zurücksetzen');
    }
  }

  /**
   * Zeigt Erfolgsdialog nach Verifizierung
   */
  private showSuccessDialog(): void {
    const dialogData: DialogData = {
      title: 'GERÄT ERFOLGREICH VERIFIZIERT!',
      svgIcon: 'file-success',
      message: `Ihr neues Gerät ist jetzt verifiziert. Es hat nun Zugriff auf Ihre verschlüsselten Nachrichten
      und wird anderen Nutzern als vertrauenswürdig angezeigt.`,
      confirmText: 'Fertig',
      confirmDataTestId: KeybackupSettingsTestIds.buttonConfirmVerification,
      dialogActionTestId: 'dialog_verification_actions',
      dialogContentTestId: 'dialog_verification_content'
    };

    this.dialogService
      .openSuccessDialog(dialogData, '28.6rem', '24rem', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.matDialog.closeAll();
        },
        error: () => {
          this.snackbarService.error('Fehler bei der Verifizierung');
        }
      });
  }
}
